import React from 'react';
import { Flex } from '@ui-library/misc';
import { ErrorProps } from '@components/sources/visualizer/mappingScreens/errorsView';
import { FixMe } from '@utils/types';
import SourceSpecificConfigEditor, { IColumn } from './sourceSpecificConfig';
import TableMapping from './tableMapping';
import { important_overrides, ISourceInfo, overrides } from './tableMapping/tableMappingProvider';
import { S3Config } from './sourceSpecificConfig/s3';
import SyncBehaviourConfig from './syncBehaviourConfig';
import { ModelConfig } from './sourceSpecificConfig/modelSelector';
import { TableConfig } from './sourceSpecificConfig/tableSelector';
import classes from '../common.module.scss';

const requiredOverrides = ['anonymous_id', 'user_id'];

export interface Config extends S3Config, ModelConfig, TableConfig {
  columns?: string[];
  mapping?: { original: string; mappedArray: string[] }[];
  constants?: { key: string; value: string }[];
  syncBehaviour?: {
    mode: string;
  };
  overridesConfig?: { [i: string]: string };
}
export interface FinalConfig {
  query: {
    table?: {
      schema?: string;
      name?: string;
    };
    columns?: string[];
  };
  table_metadata?: {
    type: string;
    sequence_column: string;
  };
  event_type: 'identify' | 'track';
  mappings?: { original: string; mappedArray: string[] }[];
  resources?: string[];
  overrides: {
    user_id?: string;
    anonymous_id?: string;
    email?: string;
    message_id?: string;
    event?: string;
  };
}
interface columnStateConfig {
  columns: string[];
  allColumns: string[];
}
interface ConfigureDataProps {
  ifUpdate?: boolean;
  defaultValue?: Config;
  accountId: string;
  sourceDefinition: FixMe;
  onChange?: (tableConfig: FinalConfig, isValid: boolean) => void;
  viewOnly?: boolean;
  schedule?: FixMe;
  updateSchemaErrors: (errorsItems: ErrorProps[]) => void;
  destSyncBehaviourOptions?: string[];
}
const ConfiguredData = ({
  defaultValue,
  onChange,
  accountId,
  sourceDefinition,
  ifUpdate,
  viewOnly,
  schedule,
  updateSchemaErrors,
  destSyncBehaviourOptions,
}: ConfigureDataProps) => {
  const skipSchemaConfiguration = sourceDefinition?.name.toLowerCase() === 's3';
  const [config, setConfig] = React.useState<Partial<Config>>(defaultValue || {});
  const [columnState, setColumnState] = React.useState<columnStateConfig>({
    columns: [],
    allColumns: [],
  });
  // const setColumnStates = (values: {
  //   columns?: string[];
  //   allColumns?: string[];
  // }) => {
  //   setColumnState({ ...columnState, ...values });
  // };
  const hasCols = !!columnState.columns && columnState.columns.length > 0;
  const setColumns = async (cols: string[]) => {
    // setAll_Columns(cols);
    setColumnState({ columns: cols, allColumns: cols });
    // setSelectedColumns(cols);
  };
  const flat_mappings = (
    mappings?: { original: string; mappedArray: string[] }[],
    map_important_overrides?: { [i: string]: string },
  ) => {
    const newMappings =
      mappings?.reduce((accum: { original: string; mapped: string }[], col) => {
        if (col.mappedArray) {
          col.mappedArray!.forEach((element) => {
            accum.push({
              original: col.original.replace(/\\\./g, '.'),
              mapped: element,
            });
          });
        }

        return accum;
      }, []) || [];

    if (map_important_overrides) {
      Object.keys(map_important_overrides).forEach((key: string) => {
        if (key !== 'user_id') {
          newMappings.unshift({
            original: map_important_overrides[key],
            mapped: key.replace(/\./g, '.'),
          });
        }
      });
      Object.keys(map_important_overrides).forEach((key: string) => {
        if (key === 'user_id') {
          newMappings.unshift({
            original: map_important_overrides[key],
            mapped: key.replace(/\./g, '.'),
          });
        }
      });
    }
    return newMappings;
  };

  const onChangeConfig = React.useCallback(
    (conf: Partial<Config>) => {
      const newConfig = { ...config, ...conf };
      setConfig(newConfig);
      if (onChange) {
        const extraConfig = { syncBehaviour: { mode: newConfig?.syncBehaviour?.mode } };
        const overridesConfig = overrides.reduce((accum: FixMe, overrideKey: string) => {
          accum[overrideKey] = overrideKey;
          return accum;
        }, {});
        let newMappings;
        if (conf?.mapping) {
          newMappings = flat_mappings(newConfig?.mapping, newConfig.overridesConfig);
        }

        const isRemovedColumns = [
          ...(newConfig.columns || []),
          ...Object.values(newConfig.overridesConfig || {}),
        ]?.some((col: string) => !columnState.columns.includes(col));
        // overridesConfig={...overridesConfig, ...newConfig.overridesConfig};
        let finalConfig: FixMe = {
          schedule,
          accountId: !newConfig.sqlModelId ? accountId : undefined,
          event_type: 'identify',
          mappings: newMappings || defaultValue?.mapping,
          overrides: overridesConfig,
          map_important_overrides: conf.overridesConfig,
          constants: conf.constants,
          sqlModelId: newConfig.sqlModelId,
          ...extraConfig,
        };

        let isValid =
          newConfig.columns &&
          newConfig.columns.length > 0 &&
          !isRemovedColumns &&
          Object.keys(newConfig.overridesConfig || {}).some((col: string) =>
            requiredOverrides.includes(col.toLowerCase()),
          );
        if (skipSchemaConfiguration) {
          finalConfig = {
            ...finalConfig,
            bucketName: config.bucketName,
            prefix: config.prefix,
            columns: newConfig.columns,
            resources: ['bucket'],
          };
          isValid = !!(isValid && (newConfig.bucketName || newConfig.sqlModelId));
        } else {
          isValid = !!(
            isValid &&
            ((newConfig.schema && newConfig.table) || newConfig.sqlModelId) &&
            newConfig.columns
          );
          if (newConfig.sqlModelId) {
            finalConfig = {
              ...finalConfig,
              query: {
                columns: newConfig.columns,
              },
              sqlModelId: newConfig.sqlModelId,
              resources: ['sql_model'],
            };
          } else {
            finalConfig = {
              ...finalConfig,
              query: {
                table: {
                  schema: newConfig.schema,
                  name: newConfig.table,
                },
                columns: newConfig.columns,
              },
              resources: [newConfig.table!],
            };
          }
        }
        onChange(finalConfig, isValid);
      }
    },
    [config, columnState.columns],
  );

  const sourceConfig: ISourceInfo = {
    accountId: accountId || config.accountId,
    schema: config.schema,
    table: config.table,
    bucketName: config.bucketName,
    prefix: config.prefix!,
    sqlModelId: config.sqlModelId,
  };

  const defaultColumns: string[] = defaultValue?.columns as string[];
  const newMapping = defaultColumns?.reduce(
    (accum: FixMe[], colName: string) => {
      const currentMapping = defaultValue?.mapping?.find(
        (m: FixMe) =>
          m.original === colName && (m.mapped !== 'user_id' || m.mapped !== 'anonymous_id'),
      );
      if (!currentMapping) {
        accum = [
          ...accum,
          {
            original: colName,
            mapped: colName,
          },
        ];
      }
      return accum;
    },
    [...(defaultValue?.mapping || [])],
  );

  const filtered_original_cols = newMapping
    ? newMapping?.reduce((acc: string[], val: FixMe) => {
        if (
          !important_overrides.includes(val.mapped.toLowerCase()) &&
          !acc.includes(val.original)
        ) {
          acc.push(val.original);
        }
        return acc;
      }, [])
    : undefined;

  const filtered_mapped_cols = defaultValue?.mapping?.reduce((acc: string[], val: FixMe) => {
    if (
      !important_overrides.includes(val.mapped.toLowerCase()) &&
      !acc.includes(val.mapped.toLowerCase())
    ) {
      acc.push(val.mapped.toLowerCase());
    }
    return acc;
  }, []);

  const selectedColumns =
    defaultColumns?.filter((col: string) => filtered_original_cols?.includes(col)) || [];

  let renamedRows =
    defaultValue?.mapping?.reduce((acc: FixMe, val: FixMe) => {
      const renamedkey = val.mapped.toLowerCase() === 'email' ? 'email' : val.mapped;
      if (!important_overrides.includes(val.mapped.toLowerCase())) {
        if (Object.prototype.hasOwnProperty.call(acc, val.original.replace(/\./g, '\\.'))) {
          acc[val.original.replace(/\./g, '\\.')].push(renamedkey);
        } else {
          acc[val.original.replace(/\./g, '\\.')] = [renamedkey];
        }
      } else {
        acc[val.original.replace(/\./g, '\\.')] = [];
      }
      return acc;
    }, {}) || [];
  const a = columnState.allColumns?.reduce((accum: FixMe, val) => {
    const renamedkey = val.toLowerCase() === 'email' ? 'email' : val;
    if (
      (!Object.prototype.hasOwnProperty.call(renamedRows, val.replace(/\./g, '\\.')) ||
        renamedRows[val.replace(/\./g, '\\.')].length === 0) &&
      !filtered_mapped_cols?.includes(renamedkey.replace(/\./g, '\\.').toLowerCase()) &&
      !important_overrides.includes(renamedkey.toLowerCase())
    ) {
      accum[val.replace(/\./g, '\\.')] = [renamedkey.replace(/\./g, '\\.')];
    }

    return accum;
  }, {});

  const defaultOverrideConfig = defaultValue?.mapping?.reduce((acc: FixMe, val: FixMe) => {
    if (important_overrides.includes(val.mapped)) {
      acc[val.mapped] = val.original;
    }
    return acc;
  }, {});
  const [loadingCols, setLoadingCols] = React.useState(false);
  const onChangeLoadingState = React.useCallback((loading) => {
    setLoadingCols(loading);
  }, []);
  const onChangeColumns = (cols: IColumn[] | string[]) => {
    const newCols = cols as string[];
    setColumns(newCols);
  };

  renamedRows = { ...renamedRows, ...a };

  const finalColumns = (() => {
    if (ifUpdate) {
      return selectedColumns;
    }
    if (selectedColumns?.length) {
      return selectedColumns;
    }
    return undefined;
  })();

  return (
    <Flex
      style={{ width: '100%', padding: '0 40px' }}
      flexDirection="column"
      alignItems="center"
      className="configure-data__container"
    >
      <div className={classes.source_sync_behaviour__container}>
        <SourceSpecificConfigEditor
          skipSchemaConfiguration={skipSchemaConfiguration}
          onChangeLoadingState={onChangeLoadingState}
          onChangeConfig={onChangeConfig}
          onChangeColumns={onChangeColumns}
          sourceDefinition={sourceDefinition}
          accountId={accountId}
          defaultValue={defaultValue}
          config={config}
          isViewOrUpdate={Boolean(ifUpdate || viewOnly)}
          updateSchemaErrors={updateSchemaErrors}
        />
        {!loadingCols && hasCols && (
          <SyncBehaviourConfig
            srcSyncBehaviourOptions={sourceDefinition?.options?.syncBehaviours || []}
            destSyncBehaviourOptions={destSyncBehaviourOptions}
            isViewOrUpdate={ifUpdate || viewOnly}
            currentMode={defaultValue?.syncBehaviour?.mode}
            setSyncBehaviour={(mode: string) => {
              onChangeConfig({ syncBehaviour: { mode } });
            }}
          />
        )}
      </div>
      <TableMapping
        loadingCols={loadingCols}
        role={sourceDefinition?.name}
        selectedColumns={finalColumns}
        renamedRows={renamedRows}
        columns={columnState.columns}
        constantsConfig={defaultValue?.constants}
        isValid={hasCols}
        sourceConfig={sourceConfig}
        setColumns={setColumns}
        overridesConfig={defaultOverrideConfig}
        updateSchemaErrors={updateSchemaErrors}
        onChange={(mapping, columns, overridesConfig, constantsConfig) => {
          onChangeConfig({
            mapping,
            columns,
            overridesConfig,
            constants: constantsConfig,
          });
        }}
        ifUpdate={Boolean(ifUpdate)}
        viewOnly={Boolean(viewOnly)}
        eventType="identify"
      />
    </Flex>
  );
};

export default ConfiguredData;
